import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import { errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const norm = (s) => String(s).toLowerCase().replace(/[^a-z0-9]/g, "");

export default function ImportDialog({ rows, fields, onImport, onClose, onDone }) {
  const [map, setMap] = useState({});
  const [busy, setBusy] = useState(false);
  const columns = rows?.length ? Object.keys(rows[0]) : [];

  useEffect(() => {
    if (!rows) return;
    const m = {};
    fields.forEach((f) => { m[f.key] = columns.find((c) => norm(c) === norm(f.key) || (f.label && norm(c) === norm(f.label))) || ""; });
    setMap(m);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const records = (rows || []).map((r) => Object.fromEntries(fields.map((f) => [f.key, map[f.key] ? String(r[map[f.key]] ?? "") : ""])));
  const count = records.filter((r) => Object.values(r).some((v) => v.trim())).length;
  const plural = (n) => `${n} card${n === 1 ? "" : "s"}`;

  const submit = async () => {
    setBusy(true);
    try {
      const inserted = await onImport(records);
      toast.success(`Imported ${plural(inserted)}`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!rows} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl bg-slate-900 border-slate-800" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Map columns to card fields</DialogTitle>
          <DialogDescription>{rows?.length} rows found · {columns.length} columns. Matching names are mapped automatically.</DialogDescription>
        </DialogHeader>
        {fields.length === 0 ? (
          <p className="text-sm text-slate-400">This design has no data fields to map.</p>
        ) : (
          <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
            {fields.map((f) => (
              <div key={f.key} className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
                <select className="cf-input h-9 text-xs" value={map[f.key] || ""} onChange={(e) => setMap({ ...map, [f.key]: e.target.value })} data-testid={`import-map-${f.key}`}>
                  <option value="">— skip —</option>
                  {columns.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <ArrowRight size={14} className="text-slate-500" />
                <span className="font-mono text-xs text-indigo-300">{f.label && f.label !== f.key && <span className="font-sans text-slate-300 mr-1.5">{f.label}</span>}{`{{${f.key}}}`}{f.type === "image" && <span className="text-slate-500"> (image)</span>}</span>
              </div>
            ))}
          </div>
        )}
        {rows?.[0] && (
          <div className="rounded-md bg-slate-950 border border-slate-800 p-3 text-[11px] font-mono text-slate-400 max-h-24 overflow-auto">
            Row 1: {JSON.stringify(rows[0])}
          </div>
        )}
        <DialogFooter>
          <button className="cf-btn-ghost h-9 text-xs" onClick={onClose} data-testid="import-cancel-button">Cancel</button>
          <button className="cf-btn-primary h-9 text-xs px-4" onClick={submit} disabled={busy || !count} data-testid="import-confirm-button">
            {busy && <Loader2 size={14} className="animate-spin" />} Import {plural(count)}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
